import { get, writable } from 'svelte/store'
import CC from 'camera-controls'
import {
  Box3,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Raycaster,
  Sphere,
  Spherical,
  Vector2,
  Vector3,
  Vector4,
  type Object3D,
  type Scene,
} from 'three'
import type { TransformControls } from 'three/examples/jsm/Addons.js'
import { ThreeItemKeys, ThreeItemTypes } from '../constants/three'

export class CameraControls extends CC {
  static #installed = false
  enableDamping: boolean = false

  constructor(element: HTMLElement, camera: PerspectiveCamera | OrthographicCamera) {
    if (!CameraControls.#installed) {
      CC.install({
        THREE: {
          Box3,
          Matrix4,
          Quaternion,
          Raycaster,
          Sphere,
          Spherical,
          Vector2,
          Vector3,
          Vector4,
        },
      })
      CameraControls.#installed = true
    }

    super(camera, element)
  }
}

export const camera = writable<PerspectiveCamera | OrthographicCamera>()
export const cameraControls = writable<CameraControls | null>(null)
export const transformControls = writable<TransformControls | null>(null)

export const isPerspectiveCamera = writable<boolean>(true)

export const setIsPerspectiveCamera = (value: boolean) => {
  isPerspectiveCamera.set(value)
}

export const togglePerspectiveCamera = () => {
  isPerspectiveCamera.set(!get(isPerspectiveCamera))
}

const focusOnBox = (box: Box3, model: string | undefined, enableTransition: boolean | undefined) => {
  const cc = get(cameraControls)
  if (!cc) return

  if (model === 'maxiprinter' && get(isPerspectiveCamera)) {
    const center = box.getCenter(new Vector3())
    const size = box.getSize(new Vector3())
    const dist = Math.max(size.x, size.y) * 1.5
    cc.setLookAt(center.x, center.y - dist * 0.3, center.z + dist, center.x, center.y, center.z, enableTransition ?? true)
  }
  else {
    cc.fitToBox(box, enableTransition ?? true, { paddingTop: 0.5, paddingBottom: 0.5, paddingLeft: 0.5, paddingRight: 0.5 })
  }
}

export const focusOnObject = (object: Object3D, enableTransition: boolean | undefined = true) => {
  const model: string | undefined = object.userData[ThreeItemKeys.PRINTER_MODEL]
  focusOnBox(new Box3().setFromObject(object), model, enableTransition)
}

// Like focusOnObject, but for framing an arbitrary bounding box (e.g. built
// gcode geometry) rather than a scene object with its own userData.
export const focusOnBounds = (box: Box3, model: string | undefined, enableTransition: boolean | undefined = true) => {
  focusOnBox(box, model, enableTransition)
}

const relevantTypes = new Set([ThreeItemTypes.PRINTER, ThreeItemTypes.FILE, ThreeItemTypes.GROUP])

export const focusOnSceneContent = (scene: Scene) => {
  const cc = get(cameraControls)
  if (!cc) return

  const box = new Box3()
  let hasMaxi = false

  scene.traverse((obj) => {
    if (relevantTypes.has(obj.userData[ThreeItemKeys.ITEM_TYPE])) {
      box.expandByObject(obj)
    }
    if (obj.userData[ThreeItemKeys.PRINTER_MODEL] === 'maxiprinter') {
      hasMaxi = true
    }
  })

  if (box.isEmpty()) return

  const center = box.getCenter(new Vector3())
  const size = box.getSize(new Vector3())

  if (hasMaxi && get(isPerspectiveCamera)) {
    const dist = Math.max(size.x, size.y) * 1.5
    cc.setLookAt(center.x, center.y - dist * 0.3, center.z + dist, center.x, center.y, center.z, false)
  }
  else {
    cc.fitToBox(box, false, { paddingTop: 0.5, paddingBottom: 0.5, paddingLeft: 0.5, paddingRight: 0.5 })
  }
}

type CameraType = 'perspective' | 'orthographic'

export const cameraType: WritableDerived<CameraType> = (() => {
  const { subscribe, set } = writable<CameraType>('perspective')

  // Keep both stores in sync
  const unsubscribe = isPerspectiveCamera.subscribe((value) => {
    set(value ? 'perspective' : 'orthographic')
  })

  return {
    subscribe,
    set: (value) => {
      isPerspectiveCamera.set(value === 'perspective')
      set(value)
    },
    update: (fn) => {
      const value = fn(get(cameraType))
      isPerspectiveCamera.set(value === 'perspective')
      set(value)
    },
    destroy: unsubscribe,
  }
})()
